import { isLabel } from 'diagram-js/lib/util/ModelUtil';
import { closest } from 'min-dom';
import { html } from 'diagram-js/lib/ui';
import { isArray, isString, isUndefined } from 'min-dash';
import { getBusinessObject } from 'bpmn-js/lib/util/ModelUtil';

const VERY_LOW_PRIORITY = 100;

/**
 * A provider that adds a button to the context pad to show available
 * refactorings.
 */
class RefactoringsContextPadProvider {
  constructor(contextPad, popupMenu, translate, refactorings, eventBus) {
    contextPad.registerProvider(VERY_LOW_PRIORITY, this);

    this._translate = translate;
    this._contextPad = contextPad;
    this._popupMenu = popupMenu;
    this._refactorings = refactorings;
    this._eventBus = eventBus;
  }

  _getPopupMenuPosition(pad) {
    const Y_OFFSET = 5;

    const bounds = pad.getBoundingClientRect();

    return {
      x: bounds.left,
      y: bounds.bottom + Y_OFFSET
    };
  }

  getContextPadEntries(element) {
    return (entries) => {
      if (isLabel(element)) {
        return entries;
      }

      const {
        delete: deleteEntry,
        ...rest
      } = entries;

      entries = {
        ...rest,
        refactorings: {
          action: {
            click: (event) => {
              const pad = this._contextPad.getPad(element);
              this._openPopupMenu(pad, element);

              const entry = closest(event.delegateTarget || event.target, '.entry', true);
              entry.classList.add('active');
              this._eventBus.once('popupMenu.closed', () => {
                entry.classList.remove('active');
              });
            }
          },
          group: 'refactorings',
          className: 'suggest-refactoring',
          html: `<div class="entry" title="${ this._translate('Show suggestions') }">
            <svg width="30" height="30" viewBox="6 6 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16.7219 13.2691L16.7212 13.2684C15.6661 12.2119 14.7435 10.4757 14.0638 9.00772C14.0445 9.00316 14.0206 9 13.9941 9C13.9675 9 13.9436 9.00316 13.9243 9.00773C13.2447 10.4757 12.322 12.2122 11.2662 13.2687C10.2106 14.325 8.47501 15.2485 7.00803 15.9287C7.00329 15.9483 7 15.9728 7 16C7 16.0271 7.00325 16.0515 7.00794 16.071C8.47467 16.751 10.2101 17.6743 11.2659 18.7311C12.322 19.7875 13.2448 21.5244 13.9245 22.9923C13.9437 22.9969 13.9675 23 13.994 23C14.0205 23 14.0444 22.9968 14.0636 22.9923C14.7433 21.5244 15.666 19.7877 16.7215 18.7315M16.7215 18.7315C17.773 17.6791 19.4531 16.7468 20.8721 16.0577C20.8756 16.0415 20.878 16.0217 20.878 15.9998C20.878 15.9779 20.8756 15.9582 20.8721 15.942C19.4537 15.2529 17.7734 14.3207 16.7219 13.2691" fill="currentcolor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M22.4808 9.52468L22.4805 9.52442C22.1037 9.14711 21.7742 8.52704 21.5315 8.00276C21.5246 8.00113 21.5161 8 21.5066 8C21.4971 8 21.4885 8.00113 21.4817 8.00276C21.2389 8.52705 20.9094 9.14721 20.5323 9.52455C20.1553 9.9018 19.5355 10.2316 19.0116 10.4745C19.0099 10.4815 19.0087 10.4903 19.0087 10.5C19.0087 10.5097 19.0099 10.5184 19.0115 10.5254C19.5354 10.7682 20.1552 11.098 20.5322 11.4754C20.9094 11.8527 21.239 12.473 21.4817 12.9972C21.4886 12.9989 21.4971 13 21.5066 13C21.516 13 21.5246 12.9989 21.5314 12.9972C21.7742 12.473 22.1037 11.8528 22.4806 11.4755M22.4806 11.4755C22.8562 11.0997 23.4562 10.7667 23.963 10.5206C23.9643 10.5148 23.9651 10.5078 23.9651 10.4999C23.9651 10.4921 23.9643 10.4851 23.963 10.4793C23.4565 10.2332 22.8563 9.90026 22.4808 9.52468" fill="currentcolor"/>
            </svg>
          </div>`
        }
      };

      if (deleteEntry) {
        entries = {
          ...entries,
          deleteEntry
        };
      }

      return entries;
    };
  }

  _openPopupMenu(pad, element) {
    this._popupMenu.open(element, 'refactoring-actions',
      this._getPopupMenuPosition(pad.html),
      {
        title: this._translate('Apply suggestions'),
        width: 350
      }
    );
  }
}

RefactoringsContextPadProvider.$inject = [
  'contextPad',
  'popupMenu',
  'translate',
  'refactorings',
  'eventBus'
];

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "@keyframes prefix--stroke {\n  100% {\n    stroke-dashoffset: 0;\n  }\n}\n:root {\n  --cds-layer: var(--cds-layer-01, #f4f4f4);\n  --cds-layer-active: var(--cds-layer-active-01, #c6c6c6);\n  --cds-layer-hover: var(--cds-layer-hover-01, #e8e8e8);\n  --cds-layer-selected: var(--cds-layer-selected-01, #e0e0e0);\n  --cds-layer-selected-hover: var(--cds-layer-selected-hover-01, #d1d1d1);\n  --cds-layer-accent: var(--cds-layer-accent-01, #e0e0e0);\n  --cds-layer-accent-hover: var(--cds-layer-accent-hover-01, #d1d1d1);\n  --cds-layer-accent-active: var(--cds-layer-accent-active-01, #a8a8a8);\n  --cds-field: var(--cds-field-01, #f4f4f4);\n  --cds-field-hover: var(--cds-field-hover-01, #e8e8e8);\n  --cds-border-subtle: var(--cds-border-subtle-00, #e0e0e0);\n  --cds-border-subtle-selected: var(--cds-border-subtle-selected-01, #c6c6c6);\n  --cds-border-strong: var(--cds-border-strong-01, #8d8d8d);\n  --cds-border-tile: var(--cds-border-tile-01, #c6c6c6);\n}\n\n.cds--layer-one {\n  --cds-layer: var(--cds-layer-01, #f4f4f4);\n  --cds-layer-active: var(--cds-layer-active-01, #c6c6c6);\n  --cds-layer-hover: var(--cds-layer-hover-01, #e8e8e8);\n  --cds-layer-selected: var(--cds-layer-selected-01, #e0e0e0);\n  --cds-layer-selected-hover: var(--cds-layer-selected-hover-01, #d1d1d1);\n  --cds-layer-accent: var(--cds-layer-accent-01, #e0e0e0);\n  --cds-layer-accent-hover: var(--cds-layer-accent-hover-01, #d1d1d1);\n  --cds-layer-accent-active: var(--cds-layer-accent-active-01, #a8a8a8);\n  --cds-field: var(--cds-field-01, #f4f4f4);\n  --cds-field-hover: var(--cds-field-hover-01, #e8e8e8);\n  --cds-border-subtle: var(--cds-border-subtle-00, #e0e0e0);\n  --cds-border-subtle-selected: var(--cds-border-subtle-selected-01, #c6c6c6);\n  --cds-border-strong: var(--cds-border-strong-01, #8d8d8d);\n  --cds-border-tile: var(--cds-border-tile-01, #c6c6c6);\n}\n\n.cds--layer-two {\n  --cds-layer: var(--cds-layer-02, #ffffff);\n  --cds-layer-active: var(--cds-layer-active-02, #c6c6c6);\n  --cds-layer-hover: var(--cds-layer-hover-02, #e8e8e8);\n  --cds-layer-selected: var(--cds-layer-selected-02, #e0e0e0);\n  --cds-layer-selected-hover: var(--cds-layer-selected-hover-02, #d1d1d1);\n  --cds-layer-accent: var(--cds-layer-accent-02, #e0e0e0);\n  --cds-layer-accent-hover: var(--cds-layer-accent-hover-02, #d1d1d1);\n  --cds-layer-accent-active: var(--cds-layer-accent-active-02, #a8a8a8);\n  --cds-field: var(--cds-field-02, #ffffff);\n  --cds-field-hover: var(--cds-field-hover-02, #e8e8e8);\n  --cds-border-subtle: var(--cds-border-subtle-01, #c6c6c6);\n  --cds-border-subtle-selected: var(--cds-border-subtle-selected-02, #c6c6c6);\n  --cds-border-strong: var(--cds-border-strong-02, #8d8d8d);\n  --cds-border-tile: var(--cds-border-tile-02, #a8a8a8);\n}\n\n.cds--layer-three {\n  --cds-layer: var(--cds-layer-03, #f4f4f4);\n  --cds-layer-active: var(--cds-layer-active-03, #c6c6c6);\n  --cds-layer-hover: var(--cds-layer-hover-03, #e8e8e8);\n  --cds-layer-selected: var(--cds-layer-selected-03, #e0e0e0);\n  --cds-layer-selected-hover: var(--cds-layer-selected-hover-03, #d1d1d1);\n  --cds-layer-accent: var(--cds-layer-accent-03, #e0e0e0);\n  --cds-layer-accent-hover: var(--cds-layer-accent-hover-03, #d1d1d1);\n  --cds-layer-accent-active: var(--cds-layer-accent-active-03, #a8a8a8);\n  --cds-field: var(--cds-field-03, #f4f4f4);\n  --cds-field-hover: var(--cds-field-hover-03, #e8e8e8);\n  --cds-border-subtle: var(--cds-border-subtle-02, #e0e0e0);\n  --cds-border-subtle-selected: var(--cds-border-subtle-selected-03, #c6c6c6);\n  --cds-border-strong: var(--cds-border-strong-03, #8d8d8d);\n  --cds-border-tile: var(--cds-border-tile-03, #c6c6c6);\n}\n\n.cds--loading {\n  box-sizing: border-box;\n  padding: 0;\n  border: 0;\n  margin: 0;\n  font-family: inherit;\n  font-size: 100%;\n  vertical-align: baseline;\n  animation-duration: 690ms;\n  animation-fill-mode: forwards;\n  animation-iteration-count: infinite;\n  animation-name: cds--rotate;\n  animation-timing-function: linear;\n  block-size: 5.5rem;\n  inline-size: 5.5rem;\n}\n.cds--loading *,\n.cds--loading *::before,\n.cds--loading *::after {\n  box-sizing: inherit;\n}\n.cds--loading svg circle {\n  animation-duration: 10ms;\n  animation-name: cds--init-stroke;\n  animation-timing-function: cubic-bezier(0.5, 0, 0.1, 1);\n}\n@media screen and (prefers-reduced-motion: reduce) {\n  .cds--loading svg circle {\n    animation: none;\n  }\n}\n\n.cds--loading__svg {\n  fill: transparent;\n}\n\n.cds--loading__svg circle {\n  stroke-dasharray: 276.4608 276.4608;\n  stroke-linecap: butt;\n  stroke-width: 10;\n}\n\n.cds--loading__stroke {\n  stroke: var(--cds-interactive, #0f62fe);\n  stroke-dashoffset: 52.527552;\n}\n\n.cds--loading--small .cds--loading__stroke {\n  stroke-dashoffset: 143.759616;\n}\n\n.cds--loading--stop {\n  animation: cds--rotate-end-p1 700ms cubic-bezier(0, 0, 0.25, 1) forwards, cds--rotate-end-p2 700ms cubic-bezier(0, 0, 0.25, 1) 700ms forwards;\n}\n.cds--loading--stop svg circle {\n  animation-delay: 700ms;\n  animation-duration: 700ms;\n  animation-fill-mode: forwards;\n  animation-name: cds--stroke-end;\n  animation-timing-function: cubic-bezier(0, 0, 0.25, 1);\n}\n@media screen and (prefers-reduced-motion: reduce) {\n  .cds--loading--stop svg circle {\n    animation: none;\n  }\n}\n\n.cds--loading--small {\n  block-size: 1rem;\n  inline-size: 1rem;\n  line-height: 1rem;\n}\n.cds--loading--small circle {\n  stroke-width: 16;\n}\n\n.cds--loading--small .cds--loading__svg {\n  stroke: var(--cds-interactive, #0f62fe);\n}\n\n.cds--loading__background {\n  stroke: var(--cds-layer-accent);\n  stroke-dashoffset: -22;\n}\n\n@media not all and (resolution >= 0.001dpcm) {\n  @supports (-webkit-appearance: none) and (stroke-color: transparent) {\n    circle.cds--loading__background {\n      stroke-dasharray: 265;\n      stroke-dashoffset: 0;\n    }\n  }\n}\n.cds--loading-overlay {\n  position: fixed;\n  z-index: 6000;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  background-color: var(--cds-overlay, rgba(22, 22, 22, 0.5));\n  block-size: 100%;\n  inline-size: 100%;\n  inset-block-start: 0;\n  inset-inline-start: 0;\n  transition: background-color 700ms cubic-bezier(0.4, 0.14, 0.3, 1);\n}\n\n.cds--loading-overlay--stop {\n  display: none;\n}\n\n@keyframes cds--rotate {\n  0% {\n    transform: rotate(0deg);\n  }\n  100% {\n    transform: rotate(360deg);\n  }\n}\n@keyframes cds--rotate-end-p1 {\n  100% {\n    transform: rotate(360deg);\n  }\n}\n@keyframes cds--rotate-end-p2 {\n  100% {\n    transform: rotate(-360deg);\n  }\n}\n/* Stroke animations */\n@keyframes cds--init-stroke {\n  0% {\n    stroke-dashoffset: 276.4608;\n  }\n  100% {\n    stroke-dashoffset: 52.527552;\n  }\n}\n@keyframes cds--stroke-end {\n  0% {\n    stroke-dashoffset: 52.527552;\n  }\n  100% {\n    stroke-dashoffset: 276.4608;\n  }\n}\n.cds--inline-loading {\n  display: flex;\n  align-items: center;\n  inline-size: 100%;\n  min-block-size: 2rem;\n}\n\n.cds--inline-loading__text {\n  font-size: var(--cds-label-01-font-size, 0.75rem);\n  font-weight: var(--cds-label-01-font-weight, 400);\n  line-height: var(--cds-label-01-line-height, 1.33333);\n  letter-spacing: var(--cds-label-01-letter-spacing, 0.32px);\n  color: var(--cds-text-secondary, #525252);\n}\n\n.cds--inline-loading__animation {\n  position: relative;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin-inline-end: 0.5rem;\n}\n\n.cds--inline-loading__checkmark-container {\n  fill: var(--cds-support-success, #24a148);\n}\n.cds--inline-loading__checkmark-container.cds--inline-loading__svg {\n  position: absolute;\n  inline-size: 0.75rem;\n  inset-block-start: 0.75rem;\n}\n.cds--inline-loading__checkmark-container[hidden] {\n  display: none;\n}\n\n.cds--inline-loading__checkmark {\n  animation-duration: 250ms;\n  animation-fill-mode: forwards;\n  animation-name: cds--stroke;\n  fill: none;\n  stroke: var(--cds-interactive, #0f62fe);\n  stroke-dasharray: 12;\n  stroke-dashoffset: 12;\n  stroke-width: 1.8;\n  transform-origin: 50% 50%;\n}\n\n.cds--inline-loading--error {\n  block-size: 1rem;\n  fill: var(--cds-support-error, #da1e28);\n  inline-size: 1rem;\n}\n.cds--inline-loading--error[hidden] {\n  display: none;\n}\n\n.cds--loading--small .cds--inline-loading__svg {\n  stroke: var(--cds-interactive, #0f62fe);\n}\n\n.cds--btn .cds--inline-loading--btn {\n  min-block-size: 0;\n}\n.cds--btn .cds--inline-loading--btn .cds--inline-loading__text {\n  font-size: var(--cds-body-short-01-font-size, 0.875rem);\n  font-weight: var(--cds-body-short-01-font-weight, 400);\n  line-height: var(--cds-body-short-01-line-height, 1.28572);\n  letter-spacing: var(--cds-body-short-01-letter-spacing, 0.16px);\n}\n\n/* If IE11 Don't show check animation */\n@media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {\n  .cds--inline-loading__checkmark-container {\n    inset-block-start: 1px;\n    inset-inline-end: 0.5rem;\n  }\n  .cds--inline-loading__checkmark {\n    animation: none;\n    stroke-dasharray: 0;\n    stroke-dashoffset: 0;\n  }\n}\n.suggest-refactoring .cds--loading {\n  line-height: 1em;\n}\n.suggest-refactoring .cds--loading--small {\n  block-size: 1em;\n  inline-size: 1em;\n}\n.suggest-refactoring .refactoring-icon {\n  width: 36px;\n  height: 36px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin: 0;\n  background: none;\n  box-shadow: none;\n  border-radius: 0;\n  padding: 6px;\n}";
styleInject(css_248z);

var ContextPad = {
  __init__: [
    'refactoringsContextPadProvider'
  ],
  refactoringsContextPadProvider: [ 'type', RefactoringsContextPadProvider ]
};

/**
 * An entry provider for the popup menu that shows available refactorings.
 */
class RefactoringsPopupMenuProvider {
  constructor(popupMenu, translate, refactorings, eventBus) {
    this._popupMenu = popupMenu;
    this._translate = translate;
    this._refactorings = refactorings;

    this._fetchedRefactorings = null;

    eventBus.on('popupMenu.closed', () => {
      this._fetchedRefactorings = null;
    });

    this.register();
  }

  async fetchRefactoringActions(element) {
    this._fetchedRefactorings = await this._refactorings.getRefactorings([ element ]);

    // TODO: temporary workaround; if promise resolves immediately, event is
    // captured by popup menu resulting in popup menu being closed; adding
    // timeout prevents popup menu from capturing event
    window.setTimeout(() => {
      this._popupMenu.refresh();
    });
  }

  register() {
    this._popupMenu.registerProvider('refactoring-actions', this);
  }

  getPopupMenuEntries(element) {
    const refactorings = this._fetchedRefactorings;

    if (!refactorings) {
      this.fetchRefactoringActions(element);
    }

    // We are still loading
    if (!isArray(refactorings)) {
      return {};
    }

    const entries = (refactorings || []).reduce((entries, refactoring) => {
      entries[ refactoring.id ] = {
        label: refactoring.label || refactoring.name,
        action: () => {
          refactoring.execute([ element ]);
        }
      };
      return entries;
    }, {});

    return entries;
  }

  getEmptyPlaceholder() {
    if (!isArray(this._fetchedRefactorings)) {
      return html`<span><${ LoadingIcon } translate=${ this._translate } /></span>`;
    } else {
      return this._translate('There are currently no suggestion available for this element. Please try again with a different element.');
    }

  }
}

RefactoringsPopupMenuProvider.$inject = [
  'popupMenu',
  'translate',
  'refactorings',
  'eventBus'
];

function LoadingIcon({ translate }) {
  return html`
    <div class="cds--layout">
      <div class="cds--inline-loading" aria-live="assertive">
        <div class="cds--inline-loading__animation">
          <div aria-atomic="true" aria-live="assertive" class="cds--loading cds--loading--small">
            <svg class="cds--loading__svg" viewBox="0 0 100 100">
              <title>Loading</title>
              <circle class="cds--loading__background" cx="50%" cy="50%" r="42"></circle>
              <circle class="cds--loading__stroke" cx="50%" cy="50%" r="42"></circle>
            </svg>
          </div>
        </div>
        <div class="cds--inline-loading__text">${translate('Loading suggestions...')}</div>
      </div>
    </div>
`;
}

var PopupMenu = {
  __init__: [
    'refactoringsPopupMenuProvider'
  ],
  refactoringsPopupMenuProvider: [ 'type', RefactoringsPopupMenuProvider ]
};

/**
 * @typedef {import('bpmn-js/lib/model/Types').Element} Element
 *
 * @typedef {{
 *   id: string;
 *   label: string;
 *   execute: (elements: Element[]) => void;
 * }} Refactoring
 *
 * @typedef {any} RefactoringProvider
 */

let Refactorings$1 = class Refactorings {
  constructor() {
    this._providers = [];
  }

  /**
   * Get refactorings for one or many elements.
   *
   * @param {Element[]} elements
   *
   * @returns {Refactoring[]}
   */
  async getRefactorings(elements) {
    const allRefactorings = [];

    // TODO: parallelize requests by using Promise.all
    for (const provider of this._providers) {
      const refactorings = await provider.getRefactorings(elements);

      if (refactorings) {
        allRefactorings.push(...refactorings);
      }
    }

    return allRefactorings;
  }

  /**
   * Register a refactoring provider.
   *
   * @param {RefactoringProvider} provider
   */
  registerProvider(provider) {
    this._providers.push(provider);
  }

  /**
   * Get all registered providers.
   *
   * @returns {RefactoringProvider[]}
   */
  getProviders() {
    return this._providers;
  }
};

Refactorings$1.$inject = [];

/**
 * @typedef {import('bpmn-js/lib/model/Types').ModdleElement} ModdleElement
 */


/**
 * @param {ModdleElement} element
 *
 * @returns {ModdleElement}
 */
function getEventDefinition(element) {
  const eventDefinitions = element.get('eventDefinitions');

  return eventDefinitions && eventDefinitions[ 0 ];
}

/**
 * @param {ModdleElement|string} elementOrType
 *
 * @returns {string}
 */
function getLocalType(elementOrType) {
  if (isString(elementOrType)) {
    return elementOrType.split(':').pop();
  }

  const { $descriptor } = elementOrType;

  const { localName } = $descriptor.ns;

  return localName;
}

/**
 * @param {ModdleElement|string} eventDefinitionOrType
 *
 * @returns {string}
 */
function getEventDefinitionPrefix(eventDefinitionOrType) {
  const localType = getLocalType(eventDefinitionOrType);

  return localType.replace('EventDefinition', '');
}

/**
 * Get type of an element or type as user-friendly string.
 *
 * @param {ModdleElement|string} elementOrType
 * @param {string} [eventDefinitionType]
 *
 * @returns {string}
 */
function typeToString(elementOrType, eventDefinitionType) {
  let type = getLocalType(elementOrType);

  if (isString(elementOrType)) {

    if (eventDefinitionType) {
      type = `${ getEventDefinitionPrefix(eventDefinitionType) }${ type }`;
    }
  } else {
    const eventDefinition = getEventDefinition(elementOrType);

    if (eventDefinition) {
      type = `${ getEventDefinitionPrefix(eventDefinition) }${ type }`;
    }
  }

  return type.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

function isEmptyString(value) {
  return isString(value) && !value.trim().length;
}

function isExactly(element, type) {
  const businessObject = getBusinessObject(element);

  const { $model } = businessObject;

  return $model.getType(businessObject.$type) === $model.getType(type);
}

function isAnyExactly(element, types) {
  return types.some(type => isExactly(element, type));
}

/**
 * @typedef {import('bpmn-js/lib/model/Types').Element} Element
 */


const DEFAULT_MODEL = 'gpt-3.5-turbo-1106';

const DEFAULT_TEMPERATURE = 0.1;

const DEFAULT_SEED = 42;

const DEFAULT_GET_USER_PROMPT = element => {
  const elementType = typeToString(getBusinessObject(element));

  const elementName = getBusinessObject(element).get('name');

  return `${ elementType } "${ elementName }"`;
};

const DEFAULT_SYSTEM_PROMPT = `Which tool can be used to refactor the
following element?`;

/**
 * OpenAI client.
 *
 * @param { {
 *   createChatCompletion: (config: Object) => Promise<Object>;
 *   getUserPrompt: (element: Element) => string;
 *   model: string;
 *   seed: number;
 *   systemPrompt: string;
 *   temperature: number;
 * } } config
 */
class OpenAIClient {
  constructor(config = {}) {
    const {
      createChatCompletion = null,
      getUserPrompt = DEFAULT_GET_USER_PROMPT,
      model = DEFAULT_MODEL,
      seed = DEFAULT_SEED,
      systemPrompt = DEFAULT_SYSTEM_PROMPT,
      temperature = DEFAULT_TEMPERATURE,
      debug = false
    } = config;

    if (!createChatCompletion) {
      throw new Error('createChatCompletion is required');
    }

    this._createChatCompletion = createChatCompletion;
    this._getUserPrompt = getUserPrompt;
    this._model = model;
    this._seed = seed;
    this._systemPrompt = systemPrompt;
    this._temperature = temperature;
    this._debug = debug;
  }

  async getToolCalls(element, tools) {
    try {
      const response = await this._createChatCompletion({
        messages: [
          {
            'role': 'system',
            'content': this._systemPrompt
          },
          {
            'role': 'user',
            'content': this._getUserPrompt(element)
          }
        ],
        model: this._model,
        seed: this._seed,
        temperature: this._temperature,
        tool_choice: 'auto',
        tools
      });

      this._debug && console.log('OpenAI response', response);

      const message = getMessage(response);

      if (this._debug && message) {
        console.log('OpenAI response message:', message);
      }

      return getTools(response);
    } catch (error) {
      console.error('OpenAI error:', error);

      return [];
    }
  }
}

function getMessage(response) {
  return response?.choices[0]?.message?.content;
}

function getTools(response) {
  const toolCalls = response?.choices[0]?.message?.tool_calls;

  if (!toolCalls) {
    return [];
  }

  return toolCalls
    .filter(toolCall => toolCall.function)
    .map(toolCall => {
      let { name, arguments: args } = toolCall.function;

      args = JSON.parse(args);

      return {
        name,
        arguments: args
      };
    });
}

/**
 * @typedef {import('bpmn-js/lib/model/Types').Element} Element
 *
 * @typedef { {
 *   type: 'function';
 *   function: {
 *     arguments?: Object;
 *     description: string;
 *     name: string;
 *   }
 * } } Tool
 *
 * @typedef { {
 *   name: string;
 *   arguments: Object;
 * } } ToolCall
 *
 * @typedef { {
 *   id: string;
 *   label: string;
 *   execute: (elements: Element[]) => void;
 * } } Refactoring
 */

const FALLBACK_TOOL_NAME = 'fallback_tool';

class OpenAIProvider {
  constructor(config = {}, eventBus, refactorings) {
    refactorings.registerProvider(this);

    const {
      debug = false,
      openai
    } = config;

    this._debug = debug;

    if (!openai) {
      console.warn('OpenAI not configured');
    }

    this._openAIClient = new OpenAIClient({
      debug,
      ...openai
    });

    this._cache = {};

    eventBus.on('element.changed', ({ element }) => {
      const businessObject = getBusinessObject(element);

      const cached = this._cache[ businessObject.get('id') ];

      if (cached && cached.name !== businessObject.get('name')) {
        delete this._cache[ businessObject.get('id') ];
      }
    });

    eventBus.on('element.updateId', ({
      element: element,
      newId: newId
    }) => {
      const businessObject = getBusinessObject(element);

      if (this._cache[ businessObject.get('id') ]) {
        this._cache[ newId ] = this._cache[ businessObject.get('id') ];

        delete this._cache[ element.id ];
      }
    });

    eventBus.on([ 'shape.removed', 'connection.removed' ], ({ element }) => {
      const businessObject = getBusinessObject(element);

      delete this._cache[ businessObject.get('id') ];
    });
  }

  /**
   * Get tools for given element. Must be implemented by provider.
   *
   * @param {Element} element
   * @param {Tool[]} tools
   *
   * @returns {Array<Function>}
   */
  _getTools(element, tools) {
    throw new Error('must implement');
  }

  /**
   * Get refactorings for given element and tool calls. Must be implemented by
   * provider.
   *
   * @param {Element} element
   * @param {ToolCall[]} toolCalls
   *
   * @returns {Refactoring[]}
   */
  _getRefactorings(element, toolCalls) {
    throw new Error('must implement');
  }

  _getCached(element) {
    return this._cache[ element.id ] && this._cache[ element.id ].request;
  }

  _setCached(element, request) {
    const businessObject = getBusinessObject(element);

    this._cache[ businessObject.get('id') ] = {
      name: getBusinessObject(element).get('name'),
      request
    };
  }

  /**
   * Get tool calls for element with given name. Caches request and returns
   * cached request if possible.
   *
   * @param {Element} element
   * @param {Tool[]} tools
   *
   * @returns {Promise<ToolCall[]>}
   */
  _getToolCalls(element, tools) {
    const cached = this._getCached(element);

    if (cached) {
      return cached;
    }

    const request = this._openAIClient.getToolCalls(element, tools);

    this._setCached(element, request);

    return request;
  }

  /**
   * Get a fallback tool that can be used if the element's name doesn't provide
   * any clear indication. OpenAI's function calling feature is heavily biased
   * towards choosing one of the tools provided, even none of them are suitable.
   *
   * @returns {Tool}
   */
  getFallbackTool() {
    return {
      type: 'function',
      function: {
        name: FALLBACK_TOOL_NAME,
        description: `Fallback tool to be used _only_ if element's name:
- Indicates no existing tool
- Indicates tool that doesn't exist (e.g. Reddit mentioned but no Reddit tool exists)
- Incomplete phrase or sentence
- Single characters or digits
- Random sequences of characters
- Inaproppriate in any other way`
      }
    };
  }

  /**
   * @param {Element[]} elements
   *
   * @returns {Refactoring[]}
   */
  async getRefactorings(elements) {

    // TODO: support more than one element
    if (elements.length !== 1) {
      return [];
    }

    const element = elements[ 0 ];

    const tools = this._getTools(element);

    this._debug && console.log('tools', tools);

    if (!tools.length) {
      return [];
    }

    let toolCalls = await this._getToolCalls(element, tools);

    this._debug && console.log('tool calls', toolCalls);

    toolCalls = toolCalls.filter((toolCall) => toolCall.name !== FALLBACK_TOOL_NAME);

    const refactorings = this._getRefactorings(element, toolCalls);

    this._debug && console.log('refactorings', refactorings);

    return refactorings;
  }
}

OpenAIProvider.$inject = [
  'config.refactorings',
  'eventBus',
  'refactorings'
];

/**
 * @typedef {import('bpmn-js/lib/model/Types').Element} Element
 *
 * @typedef { {
 *   type: 'function';
 *   function: {
 *     arguments?: Object;
 *     description: string;
 *     name: string;
 *   };
 * } } Tool
 *
 * @typedef { {
 *   name: string;
 *   arguments: Object;
 * } } ToolCall
 *
 * @typedef { {
 *   id: string;
 *   label: string;
 *   execute: (elements: Element[]) => void;
 * } } Refactoring
 */

const SYSTEM_PROMPT = `As a BPMN expert, I streamline workflows focusing on
names or abbreviations of services. If a specific service is mentioned, I will
only consider tools for that service. I will use tools not specific to any
service (e.g. HTTP/REST, GraphQL, Webhooks) as a fallback, but only if the name
implies it. Name the element for tool selection.`;

class OpenAIElementTemplatesProvider extends OpenAIProvider {
  constructor(config = {}, elementTemplates, eventBus, refactorings) {
    super({
      ...config,
      openai: {
        systemPrompt: SYSTEM_PROMPT,
        ...config.openai
      }
    }, eventBus, refactorings);

    this._elementTemplates = elementTemplates;
    this._eventBus = eventBus;
  }

  /**
   * Get tools for applying element templates to the given element. Each tool
   * corresponds to one or many element templates. Fallback tool is included to
   * handle cases where no element template can be applied.
   *
   * @param {Element} element
   *
   * @returns {Tool[]}
   */
  _getTools(element) {
    if (isUndefined(getBusinessObject(element).get('name'))
      || isEmptyString(getBusinessObject(element).get('name'))) {
      return [];
    }

    const elementTemplates = this._elementTemplates.getLatest();

    const tools = elementTemplates
      .filter((elementTemplate) => {
        return this._canApplyElementTemplate(element, elementTemplate);
      })
      .map(({ description, id, metadata = {} }) => {
        const { keywords = [] } = metadata;

        return {
          type: 'function',
          function: {
            name: idToToolName(id),
            description: getFunctionDescription(description, keywords)
          }
        };
      });

    if (!tools.length) {
      return [];
    }

    return [ ...tools, this.getFallbackTool() ];
  }

  /**
   * Get refactorings for element and tool calls. Each refactoring corresponds
   * to one element template.
   *
   * @param {Element} _
   * @param {ToolCall[]} toolCalls
   *
   * @returns {Refactoring[]}
   */
  _getRefactorings(_, toolCalls) {
    let refactorings = toolCalls.reduce((refactorings, { name }) => {
      const id = toolNameToId(name);

      const refactoring = {
        id: `element-template-${ id }`,
        label: this._getLabel(id),
        execute: (elements) => {
          if (elements.length !== 1) {
            throw new Error(`expected one element, got ${ elements.length }`);
          }

          const element = elements[ 0 ];

          this._execute(element, id);
        }
      };

      return [
        ...refactorings,
        refactoring
      ];
    }, []);

    refactorings = removeDuplicateRefactorings(refactorings);

    refactorings = sortRefactoringsAlphabetically(refactorings);

    return refactorings;
  }

  _getLabel(elementTemplateId) {
    const elementTemplate = this._getElementTemplate(elementTemplateId);

    if (!elementTemplate) {
      throw new Error(`no element template with ID ${ elementTemplateId } found`);
    }

    return `Apply ${ elementTemplate.name } template`;
  }

  _execute(element, elementTemplateId) {
    const elementTemplate = this._getElementTemplate(elementTemplateId);

    if (!elementTemplate) {
      throw new Error(`no element template with ID ${ elementTemplateId } found`);
    }

    this._eventBus.fire('refactorings.execute', {
      refactoring: {
        type: 'element-template',
        elementTemplateId
      }
    });

    this._elementTemplates.applyTemplate(element, elementTemplate);
  }

  /**
   * Check whether element template can be applied to the given element.
   *
   * @param {Element} element
   * @param {string[]} appliesTo
   *
   * @returns {boolean}
   */
  _canApplyElementTemplate(element, elementTemplate) {
    const {
      appliesTo,
      category = {}
    } = elementTemplate;

    return isAnyExactly(element, appliesTo) && category.id === 'connectors';
  }

  _getElementTemplate(elementTemplateId) {
    const latestElementTemplates = this._elementTemplates.getLatest(elementTemplateId);

    if (!latestElementTemplates) {
      return null;
    }

    const [ latestElementTemplate ] = latestElementTemplates;

    if (!latestElementTemplate) {
      return null;
    }

    return latestElementTemplate;
  }
}

OpenAIElementTemplatesProvider.$inject = [
  'config.refactorings',
  'elementTemplates',
  'eventBus',
  'refactorings'
];

function removeDuplicateRefactorings(refactorings) {
  return refactorings.filter((refactoring, index) => {
    return refactorings.findIndex(otherRefactoring => otherRefactoring.id === refactoring.id) === index;
  });
}

function sortRefactoringsAlphabetically(refactorings) {
  return refactorings.slice().sort((refactoring, otherRefactoring) => refactoring.label.localeCompare(otherRefactoring.label));
}

function idToToolName(id) {
  return id.replace(/\./g, '_');
}

function toolNameToId(name) {
  return name.replace(/_/g, '.');
}

function getFunctionDescription(description, keywords = []) {
  if (keywords.length) {
    return `# Description:
"${ description }"
# Keywords:
${ keywords.map(keyword => `- "${ keyword }"`).join('\n') }`;
  }

  return `# Description:
"${ description }"`;
}

var Refactorings = {
  __init__: [
    'refactorings',
    'openAIElementTemplatesProvider'
  ],
  refactorings: [ 'type', Refactorings$1 ],
  openAIElementTemplatesProvider: [ 'type', OpenAIElementTemplatesProvider ]
};

var index = {
  __depends__: [
    ContextPad,
    PopupMenu,
    Refactorings
  ]
};

export { index as default };
//# sourceMappingURL=index.esm.js.map
